import { useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useParams, useNavigate } from '@tanstack/react-router'
import { Topbar } from '../components/Topbar'
import { Badge } from '../components/Badge'
import {
  ArrowLeft,
  Printer,
  CheckCircle2,
  RotateCcw,
  Edit3,
  Loader2,
  Package,
  Truck,
  CreditCard,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  AlertCircle,
  Tag as TagIcon,
  Plus,
  X,
  Pin,
  Trash2,
  StickyNote,
  Copy,
  FileText,
  ClipboardList,
} from 'lucide-react'
import { apiGet, apiPost, apiPut, apiPatch, apiDelete, formatMXN } from '../lib/api'
import { paymentMethodLabel } from '../lib/payment-method-label'
import { PedidoNuevo } from './PedidoNuevo'
import { AplazoCard } from '../components/AplazoCard'
import { EnvioPanel } from '../components/EnvioPanel'

interface OrderDetail {
  id: string
  orderNumber: string
  status: string
  paymentMethod: string
  paymentStatus: string
  currency: string
  customer: {
    id: string | null
    name: string
    email: string
    phone: string
    user: {
      id: string
      name: string
      email: string
      phone: string
      discountPct: number
    } | null
  }
  totals: {
    subtotal: number
    taxRate: number
    taxTotal: number
    shippingCost: number
    discountCode: string
    discountTotal: number
    total: number
  }
  shipping: {
    street: string
    city: string
    state: string
    country: string
    zip: string
    carrier: string
    trackingCode: string
  }
  notes: string
  createdAt: string | null
  updatedAt: string | null
  items: Array<{
    id: string
    productId: string | null
    sku: string
    skuClean?: string
    name: string
    unitPrice: number
    quantity: number
    taxRate: number
    subtotal: number
    price1?: number | null
    price2?: number | null
    price3?: number | null
    price4?: number | null
  }>
}

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Borrador' },
  { value: 'pending', label: 'Pendiente' },
  { value: 'paid', label: 'Pagado' },
  { value: 'preparing', label: 'Preparando' },
  { value: 'shipped', label: 'Enviado' },
  { value: 'delivered', label: 'Entregado' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'refunded', label: 'Reembolsado' },
]

const STATUS_LABEL: Record<string, string> = Object.fromEntries(
  STATUS_OPTIONS.map((o) => [o.value, o.label]),
)

const PAYMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pendiente' },
  { value: 'paid', label: 'Pagado' },
  { value: 'failed', label: 'Fallido' },
  { value: 'refunded', label: 'Reembolsado' },
]

function statusBadge(s: string) {
  const low = (s || '').toLowerCase()
  if (low === 'delivered') return <Badge variant="success">Entregado</Badge>
  if (low === 'shipped') return <Badge variant="info">Enviado</Badge>
  if (low === 'paid') return <Badge variant="success">Pagado</Badge>
  if (low === 'preparing') return <Badge variant="warning">Preparando</Badge>
  if (low === 'pending') return <Badge variant="warning">Pendiente</Badge>
  if (low === 'cancelled') return <Badge variant="danger">Cancelado</Badge>
  if (low === 'refunded') return <Badge variant="danger">Reembolsado</Badge>
  if (low === 'draft') return <Badge>Borrador</Badge>
  return <Badge>{s || 'Sin estado'}</Badge>
}

function paymentBadge(s: string) {
  const low = (s || '').toLowerCase()
  if (low === 'paid') return <Badge variant="success">Pagado</Badge>
  if (low === 'refunded') return <Badge variant="danger">Reembolsado</Badge>
  if (low === 'failed') return <Badge variant="danger">Fallido</Badge>
  return <Badge variant="warning">Pendiente</Badge>
}

function formatDateTime(iso: string | null) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(d)
  } catch {
    return iso
  }
}

interface TimelineStep {
  key: string
  label: string
  date: string | null
  done: boolean
  current: boolean
}

function buildTimeline(order: OrderDetail): TimelineStep[] {
  const order_status_order = ['draft', 'pending', 'paid', 'preparing', 'shipped', 'delivered']
  const currentIdx = order_status_order.indexOf(order.status)
  const cancelled = order.status === 'cancelled' || order.status === 'refunded'

  const baseSteps = [
    { key: 'created', label: 'Pedido creado', date: order.createdAt },
    { key: 'paid', label: 'Pago confirmado', date: order.paymentStatus === 'paid' ? order.updatedAt : null },
    { key: 'preparing', label: 'Preparando', date: null },
    { key: 'shipped', label: 'Enviado', date: order.shipping.trackingCode ? order.updatedAt : null },
    { key: 'delivered', label: 'Entregado', date: order.status === 'delivered' ? order.updatedAt : null },
  ]

  return baseSteps.map((s, idx) => {
    const mapIdx = idx === 0 ? 1 : idx + 1 // map a order_status_order: created→pending(1), paid→2, preparing→3, shipped→4, delivered→5
    const done = !cancelled && currentIdx >= mapIdx
    const current = !cancelled && currentIdx === mapIdx
    return { ...s, done, current }
  })
}

function Card({
  title,
  icon,
  children,
  actions,
}: {
  title: string
  icon?: React.ReactNode
  children: React.ReactNode
  actions?: React.ReactNode
}) {
  return (
    <section className="rounded-2xl border border-ink-200 bg-white">
      <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          {icon}
          {title}
        </div>
        {actions}
      </header>
      <div className="p-5">{children}</div>
    </section>
  )
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 text-sm">
      <span className="text-ink-500">{label}</span>
      <span className="text-right font-medium text-ink-900">{value}</span>
    </div>
  )
}

function StatusModal({
  open,
  onClose,
  initialStatus,
  initialPaymentStatus,
  onSave,
  saving,
}: {
  open: boolean
  onClose: () => void
  initialStatus: string
  initialPaymentStatus: string
  onSave: (next: { status: string; paymentStatus: string }) => void
  saving: boolean
}) {
  const [status, setStatus] = useState(initialStatus)
  const [paymentStatus, setPaymentStatus] = useState(initialPaymentStatus)

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-950/60 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <h3 className="text-base font-semibold text-ink-900">Cambiar estado</h3>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            ×
          </button>
        </header>
        <div className="space-y-4 p-5">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-700">Estado del pedido</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
            >
              {STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-700">Estado del pago</span>
            <select
              value={paymentStatus}
              onChange={(e) => setPaymentStatus(e.target.value)}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
            >
              {PAYMENT_STATUS_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        </div>
        <footer className="flex justify-end gap-2 border-t border-ink-100 bg-ink-50 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
          >
            Cancelar
          </button>
          <button
            disabled={saving}
            onClick={() => onSave({ status, paymentStatus })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="size-3.5 animate-spin" />}
            Guardar cambios
          </button>
        </footer>
      </div>
    </div>
  )
}

// -----------------------------------------------------------------------------
// Etiquetas y notas internas
// -----------------------------------------------------------------------------

interface OrderTag {
  id: string
  orderId: string
  tag: string
  color: string
  createdAt: string
}

interface OrderNote {
  id: string
  orderId: string
  authorId: string | null
  authorName: string
  body: string
  isPinned: boolean
  createdAt: string
}

const TAG_COLORS: Array<{ value: string; label: string; cls: string }> = [
  { value: 'gray', label: 'Gris', cls: 'bg-ink-100 text-ink-800 border-ink-200' },
  { value: 'red', label: 'Rojo', cls: 'bg-red-100 text-red-800 border-red-200' },
  { value: 'orange', label: 'Naranja', cls: 'bg-orange-100 text-orange-800 border-orange-200' },
  { value: 'amber', label: 'Ámbar', cls: 'bg-amber-100 text-amber-800 border-amber-200' },
  { value: 'yellow', label: 'Amarillo', cls: 'bg-yellow-100 text-yellow-800 border-yellow-200' },
  { value: 'green', label: 'Verde', cls: 'bg-green-100 text-green-800 border-green-200' },
  { value: 'emerald', label: 'Esmeralda', cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' },
  { value: 'teal', label: 'Teal', cls: 'bg-teal-100 text-teal-800 border-teal-200' },
  { value: 'cyan', label: 'Cian', cls: 'bg-cyan-100 text-cyan-800 border-cyan-200' },
  { value: 'blue', label: 'Azul', cls: 'bg-blue-100 text-blue-800 border-blue-200' },
  { value: 'indigo', label: 'Índigo', cls: 'bg-indigo-100 text-indigo-800 border-indigo-200' },
  { value: 'purple', label: 'Morado', cls: 'bg-purple-100 text-purple-800 border-purple-200' },
  { value: 'pink', label: 'Rosa', cls: 'bg-pink-100 text-pink-800 border-pink-200' },
  { value: 'rose', label: 'Rose', cls: 'bg-rose-100 text-rose-800 border-rose-200' },
]

const TAG_COLOR_CLASS: Record<string, string> = Object.fromEntries(
  TAG_COLORS.map((c) => [c.value, c.cls]),
)

const PREDEFINED_TAGS: Array<{ tag: string; color: string }> = [
  { tag: 'VIP', color: 'purple' },
  { tag: 'mayorista', color: 'blue' },
  { tag: 'problema', color: 'red' },
  { tag: 'urgente', color: 'orange' },
  { tag: 'fragil', color: 'amber' },
  { tag: 'reembolso', color: 'rose' },
]

function tagClass(color: string): string {
  return TAG_COLOR_CLASS[color] || TAG_COLOR_CLASS.gray || 'bg-ink-100 text-ink-700'
}

function AddTagModal({
  open,
  onClose,
  onSave,
  saving,
  existingTags,
}: {
  open: boolean
  onClose: () => void
  onSave: (next: { tag: string; color: string }) => void
  saving: boolean
  existingTags: string[]
}) {
  const [selected, setSelected] = useState<string>('')
  const [custom, setCustom] = useState<string>('')
  const [color, setColor] = useState<string>('gray')

  if (!open) return null

  const finalTag = (selected || custom).trim()
  const alreadyExists = !!finalTag && existingTags.includes(finalTag)

  function handleSelectPredef(t: { tag: string; color: string }) {
    setSelected(t.tag)
    setCustom('')
    setColor(t.color)
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-950/60 p-4">
      <div className="w-full max-w-md rounded-2xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <h3 className="text-base font-semibold text-ink-900">Agregar etiqueta</h3>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="size-4" />
          </button>
        </header>
        <div className="space-y-4 p-5">
          <div>
            <span className="mb-2 block text-xs font-medium text-ink-700">Sugeridas</span>
            <div className="flex flex-wrap gap-2">
              {PREDEFINED_TAGS.map((t) => {
                const used = existingTags.includes(t.tag)
                const isSelected = selected === t.tag
                return (
                  <button
                    key={t.tag}
                    type="button"
                    disabled={used}
                    onClick={() => handleSelectPredef(t)}
                    className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors ${
                      tagClass(t.color)
                    } ${isSelected ? 'ring-2 ring-brand-500 ring-offset-1' : ''} ${
                      used ? 'cursor-not-allowed opacity-50' : 'hover:opacity-80'
                    }`}
                  >
                    {t.tag}
                  </button>
                )
              })}
            </div>
          </div>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-700">
              O escribe una etiqueta personalizada
            </span>
            <input
              type="text"
              value={custom}
              onChange={(e) => {
                setCustom(e.target.value)
                if (e.target.value) setSelected('')
              }}
              maxLength={40}
              placeholder="ej: cliente regular"
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
            />
          </label>

          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-700">Color</span>
            <div className="flex flex-wrap gap-2">
              {TAG_COLORS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setColor(c.value)}
                  title={c.label}
                  className={`size-6 rounded-full border-2 transition-all ${tagClass(c.value)} ${
                    color === c.value ? 'ring-2 ring-brand-500 ring-offset-1' : ''
                  }`}
                />
              ))}
            </div>
          </label>

          {alreadyExists && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
              Esta etiqueta ya existe en el pedido.
            </div>
          )}
        </div>
        <footer className="flex justify-end gap-2 border-t border-ink-100 bg-ink-50 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
          >
            Cancelar
          </button>
          <button
            disabled={saving || !finalTag || alreadyExists}
            onClick={() => onSave({ tag: finalTag, color })}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="size-3.5 animate-spin" />}
            Agregar
          </button>
        </footer>
      </div>
    </div>
  )
}

function OrderTagsCard({ orderId }: { orderId: string }) {
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery<{ data: OrderTag[] }>({
    queryKey: ['pedido-etiquetas', orderId],
    queryFn: () => apiGet(`/api/admin/pedidos/${orderId}/etiquetas`),
  })

  const addMut = useMutation({
    mutationFn: (payload: { tag: string; color: string }) =>
      apiPost(`/api/admin/pedidos/${orderId}/etiquetas`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedido-etiquetas', orderId] })
      setModalOpen(false)
      setError(null)
    },
    onError: (e: any) => setError(e?.message || 'Error al agregar etiqueta'),
  })

  const deleteMut = useMutation({
    mutationFn: (tagId: string) =>
      apiDelete(`/api/admin/pedidos/${orderId}/etiquetas/${tagId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedido-etiquetas', orderId] })
    },
  })

  const tags = data?.data || []

  return (
    <section className="rounded-2xl border border-ink-200 bg-white">
      <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          <TagIcon className="size-4 text-brand-600" />
          Etiquetas
        </div>
        <button
          type="button"
          onClick={() => {
            setError(null)
            setModalOpen(true)
          }}
          className="inline-flex items-center gap-1 rounded-lg border border-ink-200 bg-white px-2.5 py-1 text-xs font-medium text-ink-700 hover:bg-ink-50"
        >
          <Plus className="size-3" /> Agregar etiqueta
        </button>
      </header>
      <div className="p-5">
        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertCircle className="size-3.5" /> {error}
          </div>
        )}
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-ink-500">
            <Loader2 className="size-3.5 animate-spin" /> Cargando…
          </div>
        ) : tags.length === 0 ? (
          <p className="text-xs text-ink-500">
            Sin etiquetas. Agrega una para clasificar este pedido.
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {tags.map((t) => (
              <span
                key={t.id}
                className={`group inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-medium ${tagClass(
                  t.color,
                )}`}
              >
                {t.tag}
                <button
                  type="button"
                  onClick={() => {
                    if (confirm(`¿Quitar etiqueta "${t.tag}"?`)) deleteMut.mutate(t.id)
                  }}
                  disabled={deleteMut.isPending}
                  className="ml-1 rounded-full p-0.5 opacity-50 hover:bg-black/10 hover:opacity-100"
                  title="Quitar etiqueta"
                >
                  <X className="size-3" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>

      <AddTagModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        saving={addMut.isPending}
        existingTags={tags.map((t) => t.tag)}
        onSave={(next) => addMut.mutate(next)}
      />
    </section>
  )
}

function formatNoteDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('es-MX', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    }).format(new Date(iso))
  } catch {
    return iso
  }
}

function avatarInitials(name: string): string {
  const trimmed = (name || '').trim()
  if (!trimmed) return '?'
  const parts = trimmed.split(/\s+/).slice(0, 2)
  return parts.map((p) => p[0]?.toUpperCase() || '').join('') || '?'
}

function OrderNotesCard({ orderId }: { orderId: string }) {
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')
  const [isPinned, setIsPinned] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { data, isLoading } = useQuery<{ data: OrderNote[] }>({
    queryKey: ['pedido-notas', orderId],
    queryFn: () => apiGet(`/api/admin/pedidos/${orderId}/notas`),
  })

  const addMut = useMutation({
    mutationFn: (payload: { body: string; isPinned: boolean }) =>
      apiPost(`/api/admin/pedidos/${orderId}/notas`, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pedido-notas', orderId] })
      setBody('')
      setIsPinned(false)
      setError(null)
    },
    onError: (e: any) => setError(e?.message || 'Error al agregar nota'),
  })

  const pinMut = useMutation({
    mutationFn: (noteId: string) =>
      apiPatch(`/api/admin/pedidos/${orderId}/notas/${noteId}/pin`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pedido-notas', orderId] }),
  })

  const deleteMut = useMutation({
    mutationFn: (noteId: string) =>
      apiDelete(`/api/admin/pedidos/${orderId}/notas/${noteId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['pedido-notas', orderId] }),
  })

  const notes = data?.data || []

  return (
    <section className="rounded-2xl border border-ink-200 bg-white">
      <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-ink-900">
          <StickyNote className="size-4 text-brand-600" />
          Notas internas
        </div>
        <span className="text-xs text-ink-500">{notes.length} notas</span>
      </header>

      <div className="p-5">
        {error && (
          <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
            <AlertCircle className="size-3.5" /> {error}
          </div>
        )}

        {/* Lista de notas */}
        {isLoading ? (
          <div className="flex items-center gap-2 text-xs text-ink-500">
            <Loader2 className="size-3.5 animate-spin" /> Cargando…
          </div>
        ) : notes.length === 0 ? (
          <p className="text-xs text-ink-500">
            Aún no hay notas internas. Agrega una para registrar comentarios sobre este pedido.
          </p>
        ) : (
          <ul className="space-y-3">
            {notes.map((n) => (
              <li
                key={n.id}
                className={`rounded-lg border p-3 ${
                  n.isPinned ? 'border-amber-200 bg-amber-50' : 'border-ink-200 bg-white'
                }`}
              >
                <div className="flex items-start gap-2.5">
                  <div className="grid size-8 shrink-0 place-items-center rounded-full bg-brand-100 text-xs font-bold text-brand-700">
                    {avatarInitials(n.authorName)}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 text-xs">
                      <span className="font-semibold text-ink-900">{n.authorName}</span>
                      <span className="text-ink-500">{formatNoteDate(n.createdAt)}</span>
                      {n.isPinned && (
                        <span className="inline-flex items-center gap-0.5 text-amber-700">
                          <Pin className="size-3 fill-amber-700" /> Fijada
                        </span>
                      )}
                    </div>
                    <p className="mt-1.5 whitespace-pre-wrap break-words text-sm text-ink-800">
                      {n.body}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => pinMut.mutate(n.id)}
                      disabled={pinMut.isPending}
                      title={n.isPinned ? 'Desfijar' : 'Fijar arriba'}
                      className="rounded p-1 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
                    >
                      <Pin className={`size-3.5 ${n.isPinned ? 'fill-amber-700 text-amber-700' : ''}`} />
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm('¿Eliminar esta nota?')) deleteMut.mutate(n.id)
                      }}
                      disabled={deleteMut.isPending}
                      title="Eliminar"
                      className="rounded p-1 text-ink-500 hover:bg-red-100 hover:text-red-700"
                    >
                      <Trash2 className="size-3.5" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}

        {/* Formulario nuevo */}
        <div className="mt-4 border-t border-ink-100 pt-4">
          <textarea
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={3}
            maxLength={5000}
            placeholder="Escribe una nota interna…"
            className="w-full resize-y rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
          />
          <div className="mt-2 flex items-center justify-between gap-2">
            <label className="inline-flex items-center gap-2 text-xs text-ink-700">
              <input
                type="checkbox"
                checked={isPinned}
                onChange={(e) => setIsPinned(e.target.checked)}
                className="rounded border-ink-300"
              />
              <Pin className="size-3" /> Fijar arriba
            </label>
            <button
              type="button"
              onClick={() => addMut.mutate({ body: body.trim(), isPinned })}
              disabled={addMut.isPending || body.trim().length === 0}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {addMut.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <Plus className="size-3.5" />
              )}
              Agregar nota
            </button>
          </div>
        </div>
      </div>
    </section>
  )
}

// -----------------------------------------------------------------------------
// Editor de items: tabla con +/- cantidad, eliminar línea, agregar producto
// -----------------------------------------------------------------------------

// Estados en los que NO se permite editar items.
// (`shipped`, `delivered`, `cancelled`, `refunded` están fuera del flujo.)
const ITEMS_EDITABLE_STATUSES = new Set(['draft', 'pending', 'preparing', 'paid'])

// Pasarelas externas: si ya pagó por aquí, lo cobrado no se debe alterar.
const EXTERNAL_PAYMENT_METHODS = new Set([
  'paypal',
  'mercadopago',
  'mp_link',
  'mp_card',
  'aplazo',
])

function canEditItems(order: OrderDetail): { ok: boolean; reason: string } {
  if (!ITEMS_EDITABLE_STATUSES.has(order.status)) {
    return {
      ok: false,
      reason: `Pedido en estado "${order.status}", no es editable.`,
    }
  }
  if (
    EXTERNAL_PAYMENT_METHODS.has(order.paymentMethod) &&
    order.paymentStatus === 'paid'
  ) {
    return {
      ok: false,
      reason: 'Pagado con pasarela externa, no se puede modificar lo cobrado.',
    }
  }
  return { ok: true, reason: '' }
}

interface ProductSearchResult {
  id: string
  sku: string
  name: string
  price: number
  price2: number | null
  price3: number | null
  price4: number | null
  salePrice: number | null
  isOnSale: boolean
  stock: number
  image: string
  weightKg: number
}

// Etiquetas de los 4 niveles de precio (deben coincidir con config Price_Label_*).
const PRICE_LEVELS = [
  { level: 1 as const, label: 'Público' },
  { level: 2 as const, label: 'Mayoreo' },
  { level: 3 as const, label: 'Distribuidor' },
  { level: 4 as const, label: 'Especial' },
]
function priceByLevel(it: EditingItem, level: 1 | 2 | 3 | 4): number | null {
  return level === 1 ? it.price1 : level === 2 ? it.price2 : level === 3 ? it.price3 : it.price4
}

interface EditingItem {
  productId: string
  sku: string
  name: string
  quantity: number
  unitPrice: number
  level: 1 | 2 | 3 | 4
  price1: number | null
  price2: number | null
  price3: number | null
  price4: number | null
}

function ItemProductSearch({
  onPick,
}: {
  onPick: (p: ProductSearchResult) => void
}) {
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [debouncedQ, setDebouncedQ] = useState('')

  // Debounce
  useMemo(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 300)
    return () => clearTimeout(t)
  }, [q])

  const { data, isFetching } = useQuery<{ items: ProductSearchResult[] }>({
    queryKey: ['admin-prod-search', debouncedQ],
    queryFn: () =>
      apiGet(`/api/admin/productos/search?q=${encodeURIComponent(debouncedQ)}`),
    enabled: debouncedQ.length > 0,
    staleTime: 30_000,
  })

  return (
    <div className="relative">
      <div className="relative">
        <Plus className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-500" />
        <input
          type="search"
          value={q}
          onChange={(e) => {
            setQ(e.target.value)
            setOpen(true)
          }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          placeholder="Agregar producto: buscar por SKU o nombre…"
          className="w-full rounded-lg border border-ink-200 bg-white py-2 pl-10 pr-3 text-sm outline-none focus:border-brand-500"
        />
      </div>
      {open && debouncedQ.length > 0 && (
        <div className="absolute left-0 right-0 top-full z-30 mt-1 max-h-72 overflow-y-auto rounded-lg border border-ink-200 bg-white shadow-lg">
          {isFetching && (
            <div className="flex items-center gap-2 px-4 py-3 text-xs text-ink-500">
              <Loader2 className="size-3.5 animate-spin" /> Buscando…
            </div>
          )}
          {!isFetching && data && data.items.length === 0 && (
            <div className="px-4 py-3 text-xs text-ink-500">Sin coincidencias</div>
          )}
          {data && data.items.length > 0 && (
            <ul className="divide-y divide-ink-100">
              {data.items.map((p) => {
                const precio = p.isOnSale && p.salePrice ? p.salePrice : p.price
                return (
                  <li key={p.id}>
                    <button
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => {
                        onPick(p)
                        setQ('')
                        setOpen(false)
                      }}
                      className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-ink-50"
                    >
                      <div className="size-10 shrink-0 overflow-hidden rounded-md border border-ink-200 bg-ink-50">
                        {p.image ? (
                          <img
                            src={p.image}
                            alt={p.name}
                            className="size-full object-cover"
                          />
                        ) : (
                          <div className="grid size-full place-items-center text-ink-400">
                            <Package className="size-4" />
                          </div>
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium text-ink-900">
                          {p.name}
                        </div>
                        <div className="font-mono text-xs text-ink-500">{p.sku}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold text-ink-900">
                          {formatMXN(precio)}
                        </div>
                        <div className="text-xs text-ink-500">Stock {p.stock}</div>
                      </div>
                    </button>
                  </li>
                )
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

function EditableItemsCard({
  order,
  orderId,
}: {
  order: OrderDetail
  orderId: string
}) {
  const queryClient = useQueryClient()
  const [editMode, setEditMode] = useState(false)
  const [draft, setDraft] = useState<EditingItem[]>([])
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const editable = canEditItems(order)

  function startEdit() {
    setDraft(
      order.items.map((it) => {
        const p1 = it.price1 ?? it.unitPrice
        // Infiere el nivel actual comparando el precio del pedido con los del producto.
        let level: 1 | 2 | 3 | 4 = 1
        if (it.price2 != null && Math.abs(it.unitPrice - it.price2) < 0.005) level = 2
        else if (it.price3 != null && Math.abs(it.unitPrice - it.price3) < 0.005) level = 3
        else if (it.price4 != null && Math.abs(it.unitPrice - it.price4) < 0.005) level = 4
        return {
          productId: it.productId ?? '',
          sku: it.sku,
          name: it.name,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
          level,
          price1: p1,
          price2: it.price2 ?? null,
          price3: it.price3 ?? null,
          price4: it.price4 ?? null,
        }
      }),
    )
    setEditMode(true)
    setError(null)
    setSuccess(null)
  }

  // Cambia el nivel de precio de una línea → ajusta el precio unitario.
  function setLevel(idx: number, level: 1 | 2 | 3 | 4) {
    setDraft((prev) => {
      const next = [...prev]
      const cur = next[idx]
      if (!cur) return prev
      const p = priceByLevel(cur, level)
      next[idx] = { ...cur, level, unitPrice: p != null ? p : cur.unitPrice }
      return next
    })
  }

  function cancelEdit() {
    setEditMode(false)
    setDraft([])
    setError(null)
  }

  function addProduct(p: ProductSearchResult) {
    setDraft((prev) => {
      const idx = prev.findIndex((it) => it.productId === p.id)
      if (idx >= 0) {
        const next = [...prev]
        const cur = next[idx]
        if (cur) next[idx] = { ...cur, quantity: cur.quantity + 1 }
        return next
      }
      const precio = p.isOnSale && p.salePrice ? p.salePrice : p.price
      return [
        ...prev,
        {
          productId: p.id,
          sku: p.sku,
          name: p.name,
          quantity: 1,
          unitPrice: precio,
          level: 1 as const,
          price1: p.price,
          price2: p.price2,
          price3: p.price3,
          price4: p.price4,
        },
      ]
    })
  }

  function updateDraftLine(idx: number, patch: Partial<EditingItem>) {
    setDraft((prev) => prev.map((it, i) => (i === idx ? { ...it, ...patch } : it)))
  }

  function removeDraftLine(idx: number) {
    setDraft((prev) => prev.filter((_, i) => i !== idx))
  }

  const saveMut = useMutation({
    mutationFn: () =>
      apiPatch<{ data: OrderDetail }>(`/api/admin/pedidos/${orderId}/items`, {
        items: draft.map((it) => ({
          productId: it.productId,
          quantity: it.quantity,
          unitPrice: it.unitPrice,
        })),
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(['pedido', orderId], res)
      queryClient.invalidateQueries({ queryKey: ['pedido-notas', orderId] })
      queryClient.invalidateQueries({ queryKey: ['pedidos'] })
      setSuccess('Items actualizados correctamente')
      setEditMode(false)
      setDraft([])
      setTimeout(() => setSuccess(null), 2500)
    },
    onError: (e: any) => setError(e?.message || 'Error al guardar items'),
  })

  function handleSave() {
    setError(null)
    if (draft.length === 0) {
      setError('Debe quedar al menos un item en el pedido')
      return
    }
    for (const it of draft) {
      if (it.quantity <= 0) {
        setError(`Cantidad inválida en ${it.sku}`)
        return
      }
      if (it.unitPrice < 0) {
        setError(`Precio inválido en ${it.sku}`)
        return
      }
      if (!it.productId) {
        setError(`Item legacy sin productId: ${it.sku}. No se puede guardar.`)
        return
      }
    }
    saveMut.mutate()
  }

  const draftSubtotal = useMemo(
    () => draft.reduce((acc, it) => acc + it.unitPrice * it.quantity, 0),
    [draft],
  )

  return (
    <Card
      title={`Items del pedido (${editMode ? draft.length : order.items.length})`}
      icon={<Package className="size-4 text-brand-600" />}
      actions={
        editMode ? (
          <div className="flex gap-2">
            <button
              type="button"
              onClick={cancelEdit}
              disabled={saveMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50 disabled:opacity-50"
            >
              <X className="size-3.5" /> Cancelar
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={saveMut.isPending}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
            >
              {saveMut.isPending ? (
                <Loader2 className="size-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="size-3.5" />
              )}
              Guardar cambios
            </button>
          </div>
        ) : editable.ok ? (
          <button
            type="button"
            onClick={startEdit}
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50"
          >
            <Edit3 className="size-3.5" /> Editar items
          </button>
        ) : (
          <span
            className="text-xs text-ink-500"
            title={editable.reason}
          >
            Edición bloqueada
          </span>
        )
      }
    >
      {error && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          <AlertCircle className="size-3.5" /> {error}
        </div>
      )}
      {success && (
        <div className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
          <CheckCircle2 className="size-3.5" /> {success}
        </div>
      )}
      {!editMode && !editable.ok && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          {editable.reason}
        </div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-50">
            <tr className="text-left text-xs uppercase tracking-wider text-ink-500">
              <th className="px-4 py-2 font-medium">SKU</th>
              <th className="px-4 py-2 font-medium">Producto</th>
              <th className="px-4 py-2 font-medium text-right">P. unitario</th>
              <th className="px-4 py-2 font-medium text-right">Cant.</th>
              <th className="px-4 py-2 font-medium text-right">Subtotal</th>
              {editMode && <th className="w-10 px-3 py-2" />}
            </tr>
          </thead>
          <tbody>
            {editMode ? (
              draft.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-6 text-center text-sm text-ink-500"
                  >
                    No quedan items. Agrega productos abajo.
                  </td>
                </tr>
              ) : (
                draft.map((it, idx) => (
                  <tr key={`${it.productId}-${idx}`} className="border-t border-ink-100">
                    <td className="px-4 py-2 font-mono text-xs text-ink-700">
                      {it.sku}
                    </td>
                    <td className="px-4 py-2 text-ink-900">{it.name}</td>
                    <td className="px-4 py-2 text-right">
                      <div className="flex flex-col items-end gap-1">
                        <select
                          value={it.level}
                          onChange={(e) =>
                            setLevel(idx, Number(e.target.value) as 1 | 2 | 3 | 4)
                          }
                          title="Nivel de precio"
                          className="w-32 rounded border border-ink-200 px-1 py-0.5 text-xs outline-none focus:border-brand-500"
                        >
                          {PRICE_LEVELS.filter(
                            (L) => L.level === 1 || priceByLevel(it, L.level) != null,
                          ).map((L) => (
                            <option key={L.level} value={L.level}>
                              {L.label}
                              {priceByLevel(it, L.level) != null
                                ? ` · ${formatMXN(priceByLevel(it, L.level) as number)}`
                                : ''}
                            </option>
                          ))}
                        </select>
                        <input
                          type="number"
                          min={0}
                          step="0.01"
                          value={it.unitPrice}
                          onChange={(e) =>
                            updateDraftLine(idx, {
                              unitPrice: Math.max(0, Number(e.target.value || 0)),
                            })
                          }
                          className="w-24 rounded border border-ink-200 px-2 py-1 text-right text-sm outline-none focus:border-brand-500"
                        />
                      </div>
                    </td>
                    <td className="px-4 py-2 text-right">
                      <input
                        type="number"
                        min={1}
                        value={it.quantity}
                        onChange={(e) =>
                          updateDraftLine(idx, {
                            quantity: Math.max(1, parseInt(e.target.value || '1')),
                          })
                        }
                        className="w-16 rounded border border-ink-200 px-2 py-1 text-right text-sm outline-none focus:border-brand-500"
                      />
                    </td>
                    <td className="px-4 py-2 text-right font-semibold text-ink-900">
                      {formatMXN(it.unitPrice * it.quantity)}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        type="button"
                        onClick={() => removeDraftLine(idx)}
                        className="rounded-md p-1.5 text-red-500 hover:bg-red-50"
                        title="Quitar item"
                      >
                        <Trash2 className="size-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )
            ) : order.items.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-6 text-center text-sm text-ink-500"
                >
                  Sin items
                </td>
              </tr>
            ) : (
              order.items.map((it) => (
                <tr key={it.id} className="border-t border-ink-100">
                  <td className="px-4 py-3 font-mono text-xs text-ink-700">
                    {it.sku}
                  </td>
                  <td className="px-4 py-3 text-ink-900">{it.name}</td>
                  <td className="px-4 py-3 text-right text-ink-700">
                    {formatMXN(it.unitPrice)}
                  </td>
                  <td className="px-4 py-3 text-right text-ink-700">{it.quantity}</td>
                  <td className="px-4 py-3 text-right font-semibold text-ink-900">
                    {formatMXN(it.subtotal)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {editMode && (
        <div className="mt-4 space-y-3 border-t border-ink-100 pt-4">
          <ItemProductSearch onPick={addProduct} />
          <div className="flex items-center justify-between rounded-lg bg-ink-50 px-4 py-2 text-sm">
            <span className="text-ink-700">Subtotal (preview)</span>
            <span className="font-semibold text-ink-900">{formatMXN(draftSubtotal)}</span>
          </div>
          <p className="text-xs text-ink-500">
            Los totales finales (IVA, envío, total) los recalcula el servidor con base
            en la dirección actual del pedido.
          </p>
        </div>
      )}
    </Card>
  )
}

// -----------------------------------------------------------------------------
// Modal de edición de dirección de envío
// -----------------------------------------------------------------------------

const EDO_MX = [
  'Aguascalientes',
  'Baja California',
  'Baja California Sur',
  'Campeche',
  'Chiapas',
  'Chihuahua',
  'Ciudad de México',
  'Coahuila',
  'Colima',
  'Durango',
  'Estado de México',
  'Guanajuato',
  'Guerrero',
  'Hidalgo',
  'Jalisco',
  'Michoacán',
  'Morelos',
  'Nayarit',
  'Nuevo León',
  'Oaxaca',
  'Puebla',
  'Querétaro',
  'Quintana Roo',
  'San Luis Potosí',
  'Sinaloa',
  'Sonora',
  'Tabasco',
  'Tamaulipas',
  'Tlaxcala',
  'Veracruz',
  'Yucatán',
  'Zacatecas',
]

const SHIPPING_LOCKED_STATUSES = new Set([
  'shipped',
  'delivered',
  'cancelled',
  'refunded',
])

function EditShippingModal({
  open,
  onClose,
  order,
  orderId,
}: {
  open: boolean
  onClose: () => void
  order: OrderDetail
  orderId: string
}) {
  const queryClient = useQueryClient()
  const [street, setStreet] = useState(order.shipping.street || '')
  const [city, setCity] = useState(order.shipping.city || '')
  const [state, setState] = useState(order.shipping.state || '')
  const [zip, setZip] = useState(order.shipping.zip || '')
  const [country, setCountry] = useState(order.shipping.country || 'México')
  const [recipientName, setRecipientName] = useState(order.customer.name || '')
  const [phone, setPhone] = useState(order.customer.phone || '')
  const [error, setError] = useState<string | null>(null)

  // Reset al abrir
  useMemo(() => {
    if (open) {
      setStreet(order.shipping.street || '')
      setCity(order.shipping.city || '')
      setState(order.shipping.state || '')
      setZip(order.shipping.zip || '')
      setCountry(order.shipping.country || 'México')
      setRecipientName(order.customer.name || '')
      setPhone(order.customer.phone || '')
      setError(null)
    }
  }, [open, order])

  const saveMut = useMutation({
    mutationFn: () =>
      apiPatch<{ data: OrderDetail }>(`/api/admin/pedidos/${orderId}/shipping`, {
        street: street.trim(),
        city: city.trim(),
        state: state.trim(),
        zip: zip.trim(),
        country: country.trim() || 'México',
        recipientName: recipientName.trim(),
        phone: phone.trim(),
      }),
    onSuccess: (res) => {
      queryClient.setQueryData(['pedido', orderId], res)
      queryClient.invalidateQueries({ queryKey: ['pedido-notas', orderId] })
      onClose()
    },
    onError: (e: any) => setError(e?.message || 'Error al guardar dirección'),
  })

  if (!open) return null

  function handleSave() {
    setError(null)
    if (street.trim().length < 3) {
      setError('Calle requerida')
      return
    }
    if (city.trim().length < 2) {
      setError('Ciudad requerida')
      return
    }
    if (state.trim().length < 2) {
      setError('Estado requerido')
      return
    }
    if (!/^\d{4,6}$/.test(zip.trim())) {
      setError('CP inválido (4 a 6 dígitos)')
      return
    }
    saveMut.mutate()
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-ink-950/60 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-ink-100 px-5 py-3">
          <h3 className="text-base font-semibold text-ink-900">
            Editar dirección de envío
          </h3>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-ink-500 hover:bg-ink-100 hover:text-ink-900"
          >
            <X className="size-4" />
          </button>
        </header>
        <div className="space-y-3 p-5">
          {error && (
            <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              <AlertCircle className="size-3.5" /> {error}
            </div>
          )}
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-ink-700">
              Calle y número
            </span>
            <input
              type="text"
              value={street}
              onChange={(e) => setStreet(e.target.value)}
              className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              placeholder="Av. Reforma 123, Col. Centro"
            />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">Ciudad</span>
              <input
                type="text"
                value={city}
                onChange={(e) => setCity(e.target.value)}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">Estado</span>
              <select
                value={state}
                onChange={(e) => setState(e.target.value)}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              >
                <option value="">Selecciona…</option>
                {EDO_MX.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">CP</span>
              <input
                type="text"
                value={zip}
                onChange={(e) => setZip(e.target.value)}
                maxLength={6}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">País</span>
              <input
                type="text"
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              />
            </label>
          </div>
          <hr className="my-1 border-ink-100" />
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">
                Destinatario
              </span>
              <input
                type="text"
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              />
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-medium text-ink-700">
                Teléfono
              </span>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className="w-full rounded-lg border border-ink-200 bg-white px-3 py-2 text-sm outline-none focus:border-brand-500"
              />
            </label>
          </div>
          <p className="text-xs text-ink-500">
            Si cambia el estado o el CP, el envío y el total se recalculan
            automáticamente con base en las zonas configuradas.
          </p>
        </div>
        <footer className="flex justify-end gap-2 border-t border-ink-100 bg-ink-50 px-5 py-3">
          <button
            onClick={onClose}
            className="rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-100"
          >
            Cancelar
          </button>
          <button
            disabled={saveMut.isPending}
            onClick={handleSave}
            className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700 disabled:opacity-50"
          >
            {saveMut.isPending && <Loader2 className="size-3.5 animate-spin" />}
            Guardar dirección
          </button>
        </footer>
      </div>
    </div>
  )
}

// Bloque de guía de envío en el detalle del pedido. Guías POR PRODUCTO con
// envíos parciales: muestra los productos con su restante, permite elegir qué
// enviar y genera guías; lista las guías con tracking e impresión de etiqueta.
// La tarifa es fija, no hay UI de cotización.
function GuiaEnvioCard({ order, orderId }: { order: OrderDetail; orderId: string }) {
  return (
    <Card title="Guía de envío" icon={<Truck className="size-4 text-brand-600" />}>
      <EnvioPanel
        orderId={orderId}
        initialCarrier={order.shipping.carrier === 'fedex' ? 'fedex' : 'estafeta'}
      />
    </Card>
  )
}

export function PedidoDetalle() {
  const { id } = useParams({ strict: false }) as { id: string }
  // El path /pedidos/nuevo cae en esta misma ruta dinámica /pedidos/$id.
  // Se delega a PedidoNuevo para evitar tocar routes.tsx.
  if (id === 'nuevo') {
    return <PedidoNuevo />
  }
  return <PedidoDetalleInner id={id} />
}

function PedidoDetalleInner({ id }: { id: string }) {
  const navigate = useNavigate()
  // Feedback de "Copiado" para los botones de copiar (pedido / datos de guía).
  const [copied, setCopied] = useState<null | 'pedido' | 'guia'>(null)
  const queryClient = useQueryClient()
  const [modalOpen, setModalOpen] = useState(false)
  const [shippingModalOpen, setShippingModalOpen] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  const { data, isLoading, isError } = useQuery<{ data: OrderDetail }>({
    queryKey: ['pedido', id],
    queryFn: () => apiGet(`/api/admin/pedidos/${id}`),
  })

  const updateMut = useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      apiPut<{ data: OrderDetail }>(`/api/admin/pedidos/${id}`, payload),
    onSuccess: (res) => {
      queryClient.setQueryData(['pedido', id], res)
      queryClient.invalidateQueries({ queryKey: ['pedidos'] })
      setError(null)
      setSuccess('Pedido actualizado')
      setTimeout(() => setSuccess(null), 2500)
    },
    onError: (e: any) => {
      setSuccess(null)
      setError(e?.message || 'Error al actualizar')
    },
  })

  const order = data?.data

  const timeline = useMemo(() => (order ? buildTimeline(order) : []), [order])

  if (isLoading) {
    return (
      <>
        <Topbar title="Pedido" subtitle="Cargando…" />
        <div className="grid place-items-center p-20 text-ink-500">
          <Loader2 className="size-8 animate-spin" />
          <p className="mt-3 text-sm">Cargando pedido…</p>
        </div>
      </>
    )
  }

  if (isError || !order) {
    return (
      <>
        <Topbar title="Pedido" subtitle="No encontrado" />
        <div className="p-6">
          <div className="rounded-2xl border border-red-200 bg-red-50 p-6 text-center">
            <AlertCircle className="mx-auto size-8 text-red-500" />
            <h2 className="mt-3 text-base font-semibold text-red-700">
              No se pudo cargar el pedido
            </h2>
            <p className="mt-1 text-sm text-red-600">
              El pedido no existe o hubo un error consultando la BD.
            </p>
            <button
              onClick={() => navigate({ to: '/pedidos' })}
              className="mt-4 inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <ArrowLeft className="size-3.5" /> Volver a pedidos
            </button>
          </div>
        </div>
      </>
    )
  }

  // --- Copiar al portapapeles con feedback breve ---
  function copyToClipboard(text: string, which: 'pedido' | 'guia') {
    const done = () => {
      setCopied(which)
      window.setTimeout(() => setCopied(null), 1800)
    }
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => {
        window.prompt('Copia manual (Ctrl/Cmd+C):', text)
      })
    } else {
      window.prompt('Copia manual (Ctrl/Cmd+C):', text)
    }
  }

  // Texto del pedido completo (para pegar en WhatsApp / correo).
  const orderText = [
    `Pedido ${order.orderNumber}`,
    `Cliente: ${order.customer.name}${order.customer.phone ? ` · ${order.customer.phone}` : ''}${order.customer.email ? ` · ${order.customer.email}` : ''}`,
    '',
    ...order.items.map(
      (it) =>
        `${it.quantity} x ${it.name}${(it.skuClean || it.sku) ? ` [${it.skuClean || it.sku}]` : ''} — ${formatMXN(it.subtotal)}`,
    ),
    '',
    `Subtotal: ${formatMXN(order.totals.subtotal)}`,
    order.totals.discountTotal > 0
      ? `Descuento: -${formatMXN(order.totals.discountTotal)}`
      : '',
    order.totals.shippingCost > 0 ? `Envío: ${formatMXN(order.totals.shippingCost)}` : '',
    `IVA: ${formatMXN(order.totals.taxTotal)}`,
    `TOTAL: ${formatMXN(order.totals.total)}`,
    '',
    `Pago: ${paymentMethodLabel(order.paymentMethod)} (${order.paymentStatus})`,
    `Estado: ${order.status}`,
  ].join('\n')

  // Datos del destinatario para crear la guía manualmente en la paquetería.
  const shippingText = [
    order.customer.name,
    order.shipping.street,
    [order.shipping.city, order.shipping.state, order.shipping.zip]
      .filter(Boolean)
      .join(', '),
    order.shipping.country,
    order.customer.phone ? `Tel: ${order.customer.phone}` : '',
    `Pedido: ${order.orderNumber}`,
  ]
    .filter(Boolean)
    .join('\n')

  return (
    <>
      <Topbar
        title={`Pedido #${order.orderNumber}`}
        subtitle={`Creado el ${formatDateTime(order.createdAt)}`}
        actions={
          <>
            <button
              onClick={() => navigate({ to: '/pedidos' })}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <ArrowLeft className="size-3.5" /> Volver
            </button>
            <button
              onClick={() => window.open(`/api/admin/pedidos/${id}/guia?tipo=nota`, '_blank')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <FileText className="size-3.5" /> Nota de venta
            </button>
            <button
              onClick={() => window.open(`/api/admin/pedidos/${id}/guia`, '_blank')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <Printer className="size-3.5" /> Imprimir guía
            </button>
            <button
              onClick={() => copyToClipboard(orderText, 'pedido')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
            >
              <Copy className="size-3.5" /> {copied === 'pedido' ? '✓ Copiado' : 'Copiar pedido'}
            </button>
            <button
              onClick={() => copyToClipboard(shippingText, 'guia')}
              className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-700 hover:bg-ink-50"
              title="Copia nombre, dirección y teléfono para crear la guía en la paquetería"
            >
              <ClipboardList className="size-3.5" /> {copied === 'guia' ? '✓ Copiado' : 'Datos para la guía'}
            </button>
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1.5 rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-700"
            >
              <Edit3 className="size-3.5" /> Cambiar estado
            </button>
          </>
        }
      />
      <div className="space-y-4 p-6">
        {error && (
          <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-700">
            <AlertCircle className="size-4" /> {error}
          </div>
        )}
        {success && (
          <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-2 text-sm text-emerald-700">
            <CheckCircle2 className="size-4" /> {success}
          </div>
        )}

        {/* Etiquetas — arriba para clasificación rápida */}
        <OrderTagsCard orderId={id} />

        {/* Acciones rápidas */}
        <div className="flex flex-wrap gap-2">
          <button
            disabled={updateMut.isPending || order.paymentStatus === 'paid'}
            onClick={() =>
              updateMut.mutate({
                paymentStatus: 'paid',
                status: order.status === 'pending' || order.status === 'draft' ? 'paid' : order.status,
              })
            }
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
          >
            <CheckCircle2 className="size-3.5" /> Marcar como pagado
          </button>
          <button
            disabled
            title="Próximamente"
            className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-sm font-medium text-ink-500 opacity-60"
          >
            <RotateCcw className="size-3.5" /> Refund
          </button>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {/* Resumen */}
          <Card title="Resumen" icon={<Package className="size-4 text-brand-600" />}>
            <div className="mb-3 flex items-center gap-2">
              {statusBadge(order.status)}
              {paymentBadge(order.paymentStatus)}
            </div>
            <Row label="Nro. de pedido" value={`#${order.orderNumber}`} />
            <Row label="Moneda" value={order.currency} />
            <Row label="Cliente" value={order.customer.name || '—'} />
            <Row
              label="Email"
              value={
                <span className="break-all text-right">{order.customer.email || '—'}</span>
              }
            />
            <Row label="Teléfono" value={order.customer.phone || '—'} />
            <hr className="my-3 border-ink-100" />
            <Row label="Subtotal" value={formatMXN(order.totals.subtotal)} />
            {order.totals.discountTotal > 0 && (
              <Row
                label={`Descuento${order.totals.discountCode ? ` (${order.totals.discountCode})` : ''}`}
                value={`- ${formatMXN(order.totals.discountTotal)}`}
              />
            )}
            <Row
              label={`IVA (${(order.totals.taxRate * 100).toFixed(0)}%)`}
              value={formatMXN(order.totals.taxTotal)}
            />
            <Row label="Envío" value={formatMXN(order.totals.shippingCost)} />
            <hr className="my-3 border-ink-100" />
            <div className="flex items-center justify-between py-2 text-base">
              <span className="font-semibold text-ink-900">Total</span>
              <span className="font-bold text-ink-900">{formatMXN(order.totals.total)}</span>
            </div>
          </Card>

          {/* Cliente */}
          <Card title="Cliente" icon={<User className="size-4 text-brand-600" />}>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-ink-900">
                <User className="size-3.5 text-ink-500" />
                <span className="font-medium">{order.customer.name || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-ink-700">
                <Mail className="size-3.5 text-ink-500" />
                <span className="break-all">{order.customer.email || '—'}</span>
              </div>
              <div className="flex items-center gap-2 text-ink-700">
                <Phone className="size-3.5 text-ink-500" />
                {order.customer.phone || '—'}
              </div>
              {order.customer.user && (
                <div className="mt-3 rounded-lg bg-ink-50 p-3 text-xs">
                  <div className="mb-1 font-semibold text-ink-700">Cuenta registrada</div>
                  <div className="text-ink-600">ID: {order.customer.user.id}</div>
                  {order.customer.user.discountPct > 0 && (
                    <div className="mt-1">
                      <Badge variant="info">
                        Descuento {order.customer.user.discountPct}%
                      </Badge>
                    </div>
                  )}
                </div>
              )}
              {!order.customer.user && (
                <div className="mt-3 rounded-lg bg-ink-50 p-3 text-xs text-ink-500">
                  Compra como invitado
                </div>
              )}
            </div>
          </Card>

          {/* Pago */}
          <Card title="Pago" icon={<CreditCard className="size-4 text-brand-600" />}>
            <div className="space-y-1">
              <Row
                label="Método"
                value={<span>{paymentMethodLabel(order.paymentMethod)}</span>}
              />
              <Row label="Estado pago" value={paymentBadge(order.paymentStatus)} />
              <Row label="Estado pedido" value={statusBadge(order.status)} />
              <Row
                label="Creado"
                value={
                  <span className="inline-flex items-center gap-1">
                    <Calendar className="size-3 text-ink-400" />
                    {formatDateTime(order.createdAt)}
                  </span>
                }
              />
              <Row label="Actualizado" value={formatDateTime(order.updatedAt)} />
            </div>
          </Card>
        </div>

        {/* Items — editable según status/payment */}
        <EditableItemsCard order={order} orderId={id} />

        {/* Aplazo — solo si el método de pago es Aplazo */}
        {order.paymentMethod === 'aplazo' && <AplazoCard orderId={id} />}

        <div className="grid gap-4 lg:grid-cols-2">
          {/* Envío */}
          <Card
            title="Envío"
            icon={<Truck className="size-4 text-brand-600" />}
            actions={
              SHIPPING_LOCKED_STATUSES.has(order.status) ? (
                <span
                  className="text-xs text-ink-500"
                  title={`Pedido en estado "${order.status}": no editable`}
                >
                  Bloqueado
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setShippingModalOpen(true)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 bg-white px-3 py-1.5 text-xs font-medium text-ink-700 hover:bg-ink-50"
                >
                  <Edit3 className="size-3.5" /> Editar dirección
                </button>
              )
            }
          >
            <div className="space-y-1">
              <div className="flex items-start gap-2 text-sm">
                <MapPin className="mt-0.5 size-3.5 text-ink-500" />
                <div className="text-ink-900">
                  <div>{order.shipping.street || '—'}</div>
                  <div className="text-ink-700">
                    {[order.shipping.city, order.shipping.state, order.shipping.zip]
                      .filter(Boolean)
                      .join(', ') || '—'}
                  </div>
                  <div className="text-xs text-ink-500">{order.shipping.country}</div>
                </div>
              </div>
              <hr className="my-3 border-ink-100" />
              <Row label="Transportista" value={order.shipping.carrier || '—'} />
              <Row
                label="Tracking"
                value={
                  order.shipping.trackingCode ? (
                    <span className="font-mono text-xs">{order.shipping.trackingCode}</span>
                  ) : (
                    '—'
                  )
                }
              />
            </div>
          </Card>

          {/* Timeline */}
          <Card title="Línea de tiempo" icon={<Calendar className="size-4 text-brand-600" />}>
            <ol className="relative space-y-4">
              {timeline.map((step, idx) => (
                <li key={step.key} className="relative flex gap-3 pl-1">
                  <div
                    className={`mt-0.5 size-4 shrink-0 rounded-full border-2 ${
                      step.done
                        ? 'border-brand-600 bg-brand-600'
                        : step.current
                          ? 'border-brand-600 bg-white'
                          : 'border-ink-200 bg-white'
                    }`}
                  />
                  {idx < timeline.length - 1 && (
                    <div
                      className={`absolute left-2 top-5 h-full w-px ${
                        step.done ? 'bg-brand-600' : 'bg-ink-200'
                      }`}
                    />
                  )}
                  <div className="flex-1 pb-3">
                    <div
                      className={`text-sm font-medium ${
                        step.done || step.current ? 'text-ink-900' : 'text-ink-500'
                      }`}
                    >
                      {step.label}
                    </div>
                    <div className="text-xs text-ink-500">
                      {step.date ? formatDateTime(step.date) : 'Pendiente'}
                    </div>
                  </div>
                </li>
              ))}
              {(order.status === 'cancelled' || order.status === 'refunded') && (
                <li className="flex gap-3 pl-1">
                  <div className="mt-0.5 size-4 shrink-0 rounded-full border-2 border-red-500 bg-red-500" />
                  <div className="flex-1">
                    <div className="text-sm font-medium text-red-700">
                      {STATUS_LABEL[order.status]}
                    </div>
                    <div className="text-xs text-ink-500">{formatDateTime(order.updatedAt)}</div>
                  </div>
                </li>
              )}
            </ol>
          </Card>
        </div>

        {/* Guía de envío — productos por enviar, guías parciales, imprimir etiqueta */}
        <GuiaEnvioCard order={order} orderId={id} />

        {/* Nota del pedido (campo legacy `notes` en orders — comentario del cliente al hacer checkout) */}
        {order.notes && (
          <Card title="Comentario del cliente" icon={<Edit3 className="size-4 text-brand-600" />}>
            <p className="whitespace-pre-wrap text-sm text-ink-700">{order.notes}</p>
          </Card>
        )}

        {/* Notas internas — para comunicación entre admins/operadores */}
        <OrderNotesCard orderId={id} />
      </div>

      <StatusModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        initialStatus={order.status}
        initialPaymentStatus={order.paymentStatus}
        saving={updateMut.isPending}
        onSave={(next) => {
          updateMut.mutate(
            {
              status: next.status,
              paymentStatus: next.paymentStatus,
            },
            { onSuccess: () => setModalOpen(false) },
          )
        }}
      />

      <EditShippingModal
        open={shippingModalOpen}
        onClose={() => setShippingModalOpen(false)}
        order={order}
        orderId={id}
      />
    </>
  )
}

